import {
  saleDetailsFragment,
  saleFragment,
  voucherFragment
} from "@saleor/fragments/discounts";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import { fragmentMoney } from "@saleor/fragments/products";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import {
  BulkVoucherList,
  BulkVoucherListVariables
} from "./types/BulkVoucherList";
import {
  BulkVoucherListDownload,
  BulkVoucherListDownloadVariables
} from "./types/BulkVoucherListDownload";
import {
  BulkVoucherTabList,
  BulkVoucherTabListVariables
} from "./types/BulkVoucherTabList";
import {
  ExportSaleErrorVariantStatus,
  ExportSaleErrorVariantStatusVariables
} from "./types/ExportSaleErrorVariantStatus";
// import { TypedQuery } from "../queries";
import { SaleDetails, SaleDetailsVariables } from "./types/SaleDetails";
import { SaleList, SaleListVariables } from "./types/SaleList";
import {
  SaleProductDetails,
  SaleProductDetailsVariables
} from "./types/SaleProductDetails";
import { SaleValidate, SaleValidateVariables } from "./types/SaleValidate";
import {
  SaleVariantDetails,
  SaleVariantDetailsVariables
} from "./types/SaleVariantDetails";
import {
  VoucherBrandGroupsDetail,
  VoucherBrandGroupsDetailVariables
} from "./types/VoucherBrandGroupsDetail";
import {
  VoucherCustomerDetails,
  VoucherCustomerDetailsVariables
} from "./types/VoucherCustomerDetails";
import {
  VoucherDetails,
  VoucherDetailsVariables
} from "./types/VoucherDetails";
import {
  VoucherGroupList,
  VoucherGroupListVariables
} from "./types/VoucherGroupList";
import { VoucherList, VoucherListVariables } from "./types/VoucherList";
import {
  VoucherProductDetails,
  VoucherProductDetailsVariables
} from "./types/VoucherProductDetails";
import {
  VoucherVariantDetails,
  VoucherVariantDetailsVariables
} from "./types/VoucherVariantDetails";

export const saleList = gql`
  ${pageInfoFragment}
  ${saleFragment}
  query SaleList(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $filter: SaleFilterInput
    $sort: SaleSortingInput
  ) {
    sales(
      after: $after
      before: $before
      first: $first
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      edges {
        node {
          ...SaleFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const useSaleListQuery = makeQuery<SaleList, SaleListVariables>(
  saleList
);

export const voucherList = gql`
  ${pageInfoFragment}
  ${voucherFragment}
  query VoucherList(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $filter: VoucherFilterInput
    $sort: VoucherSortingInput
  ) {
    vouchers(
      after: $after
      before: $before
      first: $first
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      edges {
        node {
          ...VoucherFragment
          isHidden
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const useVoucherListQuery = makeQuery<VoucherList, VoucherListVariables>(
  voucherList
);

export const bulkVoucherListDownload = gql`
  ${pageInfoFragment}
  query BulkVoucherListDownload(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherGroup(id: $id) {
      id
      code
      vouchers(after: $after, before: $before, first: $first, last: $last) {
        totalCount
        edges {
          node {
            code
            startDate
            endDate
            discountValue
            discountValueType
            type
            verticalType
            status
            isHidden
            usageLimit
            used
            isLimitToNewCustomer
            applyOncePerCustomer
            minSpent {
              currency
              amount
            }
            group {
              code
            }
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;
export const useBulkVoucherListDownload = makeQuery<
  BulkVoucherListDownload,
  BulkVoucherListDownloadVariables
>(bulkVoucherListDownload);

export const bulkVoucherList = gql`
  ${pageInfoFragment}
  ${voucherFragment}
  query BulkVoucherList(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherGroup(id: $id) {
      id
      code
      vouchers(after: $after, before: $before, first: $first, last: $last) {
        edges {
          node {
            ...VoucherFragment
            isHidden
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;

export const useBulkVoucherListQuery = makeQuery<
  BulkVoucherList,
  BulkVoucherListVariables
>(bulkVoucherList);

export const voucherGroupList = gql`
  query VoucherGroupList($id: ID!) {
    voucherGroup(id: $id) {
      id
      code
    }
  }
`;

export const useVoucherGroupListQuery = makeQuery<
  VoucherGroupList,
  VoucherGroupListVariables
>(voucherGroupList);

export const bulkVoucherTabList = gql`
  ${pageInfoFragment}
  query BulkVoucherTabList(
    $first: Int
    $last: Int
    $filter: VoucherGroupFilterInput
    $sortBy: VoucherGroupSortingInput
    $before: String
    $after: String
    $query: String
  ) {
    voucherGroups(
      first: $first
      last: $last
      filter: $filter
      sortBy: $sortBy
      before: $before
      after: $after
      query: $query
    ) {
      edges {
        node {
          id
          code
          vouchers(first: 1) {
            edges {
              node {
                id
                code
                discountValue
                discountValueType
                minSpent {
                  amount
                  currency
                }
                startDate
                endDate
                status
                type
                maxDiscountValue
              }
            }
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const useBulkVoucherTabListQuery = makeQuery<
  BulkVoucherTabList,
  BulkVoucherTabListVariables
>(bulkVoucherTabList);

export const saleDetails = gql`
  ${saleDetailsFragment}
  query SaleDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    sale(id: $id) {
      ...SaleDetailsFragment
    }
  }
`;
export const useSaleDetails = makeQuery<SaleDetails, SaleDetailsVariables>(
  saleDetails
);

export const saleProductDetails = gql`
  ${pageInfoFragment}
  query SaleProductDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    sale(id: $id) {
      id
      products(after: $after, before: $before, first: $first, last: $last) {
        edges {
          node {
            id
            name
            isPublished
            productStatus
            productType {
              id
              name
            }
            thumbnail {
              url
            }
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
        totalCount
      }
    }
  }
`;
export const useSaleProductDetails = makeQuery<
  SaleProductDetails,
  SaleProductDetailsVariables
>(saleProductDetails);

export const saleVariantDetails = gql`
  ${fragmentMoney}
  ${pageInfoFragment}
  query SaleVariantDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    sale(id: $id) {
      id
      productVariants(
        after: $after
        before: $before
        first: $first
        last: $last
      ) {
        edges {
          node {
            id
            name
            product {
              id
              name
              productType {
                name
              }
              productStatus
              isPublished
              isSimpleProduct
            }
            sku
            price {
              ...Money
            }
            sellingPrice(saleId: $id) {
              ...Money
            }
            tenantShare(saleId: $id)
            smoShare(saleId: $id)
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
        totalCount
      }
    }
  }
`;
export const useSaleVariantDetails = makeQuery<
  SaleVariantDetails,
  SaleVariantDetailsVariables
>(saleVariantDetails);

const voucherDetails = gql`
  ${pageInfoFragment}
  ${voucherFragment}
  query VoucherDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucher(id: $id) {
      ...VoucherFragment
      type
      code
      isHidden
      usageLimit
      used
      isAppliedOnEligibleItems
      isSpecificCustomer
      isLimitToNewCustomer
      isMinEligibleOnly
      applyOncePerOrder
      applyOncePerCustomer
      smopDiscountShare
      tenantDiscountShare
      paymentMethod
      voucherConditions(first: 100) {
        edges {
          node {
            id
            metric
            operation
            value
          }
        }
      }
      deliveryTypes
      imageUrl {
        url
      }
      products(first: 100) {
        edges {
          node {
            id
            brand {
              id
              name
            }
          }
        }
      }
      malls(after: $after, before: $before, first: $first, last: $last) {
        edges {
          node {
            id
            name
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
      collections(first: $first, last: $last) {
        edges {
          node {
            id
            name
            products {
              totalCount
            }
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
      categories(first: $first, last: $last) {
        edges {
          node {
            id
            name
            products {
              totalCount
            }
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
      group {
        id
        code
      }
    }
  }
`;
// export const TypedVoucherDetails = TypedQuery<
//   VoucherDetails,
//   VoucherDetailsVariables
// >(voucherDetails);

export const useVoucherDetails = makeQuery<
  VoucherDetails,
  VoucherDetailsVariables
>(voucherDetails);

const saleValidate = gql`
  query SaleValidate($id: ID!) {
    saleValidate(id: $id) {
      errorVariantsCount
    }
  }
`;

export const useSaleValidate = makeQuery<SaleValidate, SaleValidateVariables>(
  saleValidate
);

const exportSaleErrorVariantStatus = gql`
  query ExportSaleErrorVariantStatus($id: ID!) {
    exportFile(id: $id) {
      id
      status
      url
      createdAt
    }
  }
`;

export const useExportSaleErrorVariantStatus = makeQuery<
  ExportSaleErrorVariantStatus,
  ExportSaleErrorVariantStatusVariables
>(exportSaleErrorVariantStatus);

const voucherProductDetails = gql`
  ${pageInfoFragment}
  query VoucherProductDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucher(id: $id) {
      id
      products(after: $after, before: $before, first: $first, last: $last) {
        edges {
          node {
            id
            name
            productStatus
            productType {
              id
              name
            }
            isPublished
            thumbnail {
              url
            }
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;

export const useVoucherProductDetails = makeQuery<
  VoucherProductDetails,
  VoucherProductDetailsVariables
>(voucherProductDetails);

const voucherVariantDetails = gql`
  ${pageInfoFragment}
  query VoucherVariantDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $query: String
  ) {
    voucher(id: $id) {
      id
      productVariants(
        after: $after
        before: $before
        first: $first
        last: $last
        filter: { search: $query }
      ) {
        edges {
          node {
            id
            name
            product {
              id
              name
              productType {
                name
              }
              productStatus
              isPublished
              isSimpleProduct
            }
            sku
            price {
              amount
            }
            sellingPrice(saleId: $id) {
              amount
            }
            tenantShare(voucherId: $id)
            smoShare(voucherId: $id)
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;

export const useVoucherVariantDetails = makeQuery<
  VoucherVariantDetails,
  VoucherVariantDetailsVariables
>(voucherVariantDetails);

const voucherCustomerDetails = gql`
  ${pageInfoFragment}
  query VoucherCustomerDetails(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucher(id: $id) {
      id
      eligibleCustomers(
        after: $after
        before: $before
        first: $first
        last: $last
      ) {
        edges {
          node {
            id
            firstName
            lastName
            email
            contactNo
          }
        }
        totalCount
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;

export const useVoucherCustomerDetails = makeQuery<
  VoucherCustomerDetails,
  VoucherCustomerDetailsVariables
>(voucherCustomerDetails);

const voucherBrandGroupsDetail = gql`
  query VoucherBrandGroupsDetail($id: ID!) {
    voucher(id: $id) {
      voucherTenants {
        id
        smoDiscountShare
        tenantDiscountShare
        tenant {
          tenantType
          mall {
            id
            name
          }
          brand {
            legalName
            name
            id
          }
        }
      }
    }
  }
`;

export const useVoucherBrandGroupsDetail = makeQuery<
  VoucherBrandGroupsDetail,
  VoucherBrandGroupsDetailVariables
>(voucherBrandGroupsDetail);
